import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Typography from "../../../Components/Typography";
import { AccessLogItem, AccessLogPeriod } from "../../../global/types/accessLog";
import { listAdminAccessLogs } from "../../../services/accessLogs";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { TabPanelProps } from "./UserProfileTabs";

const DEFAULT_ROWS_PER_PAGE = 25;

const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const ACCESS_LOG_RANGE_OPTIONS = [7, 30] as const;

type AccessLogRangeDays = (typeof ACCESS_LOG_RANGE_OPTIONS)[number];

const DEFAULT_ACCESS_LOG_RANGE_DAYS: AccessLogRangeDays = 7;

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLastDaysRange = (
  days: number
): { data_inicio: string; data_fim: string } => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end.getTime() - (days - 1) * DAY_IN_MS);
  return { data_inicio: toIsoDate(start), data_fim: toIsoDate(end) };
};

const getAccessLogWindowDays = (period: AccessLogPeriod): number | null => {
  const start = Date.parse(`${period.de}T00:00:00Z`);
  const end = Date.parse(`${period.ate}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  const days = Math.round((end - start) / DAY_IN_MS) + 1;
  return days > 0 ? days : null;
};

const formatAccessLogDate = (value: string, timeZone: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
};

const getAccessLogsErrorMessage = (error: unknown) => {
  if (axios.isAxiosError<{ detail?: string }>(error)) {
    if (error.response?.status === 502) {
      return (
        error.response.data?.detail ||
        "Eventos de login indisponíveis. Verifique a configuração do Keycloak."
      );
    }
  }

  return getApiErrorMessage(error);
};

interface MetricItemProps {
  label: string;
  value: number;
}

function MetricItem({ label, value }: MetricItemProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        minWidth: 180,
        p: 2,
      }}
    >
      <Typography variant="miniText" className="text-[#676A70]">
        {label}
      </Typography>
      <Typography variant="h4">{value.toLocaleString("pt-BR")}</Typography>
    </Paper>
  );
}

export default function AccessLogsTab(props: TabPanelProps) {
  const { value, index } = props;
  const timeZone = useMemo(getBrowserTimeZone, []);
  const [items, setItems] = useState<AccessLogItem[]>([]);
  const [totalAccesses, setTotalAccesses] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [windowDays, setWindowDays] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [rangeDays, setRangeDays] = useState<AccessLogRangeDays>(
    DEFAULT_ACCESS_LOG_RANGE_DAYS
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestFetchIdRef = useRef(0);

  const fetchAccessLogs = useCallback(async () => {
    const fetchId = latestFetchIdRef.current + 1;
    latestFetchIdRef.current = fetchId;

    setLoading(true);
    setError(null);

    try {
      const { data_inicio, data_fim } = getLastDaysRange(rangeDays);
      const response = await listAdminAccessLogs({
        tz: timeZone,
        data_inicio,
        data_fim,
        page: page + 1,
        size: rowsPerPage,
      });

      if (fetchId !== latestFetchIdRef.current) return;

      setItems(response.itens);
      setTotalAccesses(response.total_acessos);
      setUniqueUsers(response.usuarios_unicos);
      setTotalItems(response.pagina.total_itens);
      setWindowDays(getAccessLogWindowDays(response.periodo));
    } catch (accessLogsError) {
      if (fetchId !== latestFetchIdRef.current) return;

      setItems([]);
      setTotalAccesses(0);
      setUniqueUsers(0);
      setTotalItems(0);
      setWindowDays(null);
      setError(getAccessLogsErrorMessage(accessLogsError));
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, rangeDays, rowsPerPage, timeZone]);

  useEffect(() => {
    if (value === index) {
      fetchAccessLogs();
    }
  }, [fetchAccessLogs, index, value]);

  const handlePageChange = (_event: unknown, nextPage: number) => {
    setPage(nextPage);
  };

  const handleRangeDaysChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextRangeDays: AccessLogRangeDays | null
  ) => {
    if (!nextRangeDays) return;

    setRangeDays(nextRangeDays);
    setPage(0);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4">Acessos</Typography>
            <Typography variant="body4" className="text-[#676A70] pt-2">
              {windowDays
                ? `Logins bem-sucedidos dos últimos ${windowDays} ${
                    windowDays === 1 ? "dia" : "dias"
                  }.`
                : "Logins bem-sucedidos no período configurado."}
            </Typography>
          </Box>

          <ToggleButtonGroup
            aria-label="Intervalo de acessos"
            exclusive
            onChange={handleRangeDaysChange}
            size="small"
            sx={{ mb: 3 }}
            value={rangeDays}
          >
            {ACCESS_LOG_RANGE_OPTIONS.map((days) => (
              <ToggleButton key={days} value={days}>
                {`${days} dias`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box
            aria-label="Resumo de acessos"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mb: 3,
            }}
          >
            <MetricItem label="Total de acessos" value={totalAccesses} />
            <MetricItem label="Usuários únicos" value={uniqueUsers} />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Data/hora</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell align="center" colSpan={2} sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell align="center" colSpan={2} sx={{ py: 4 }}>
                      <Typography variant="body4">{error}</Typography>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={2} sx={{ py: 4 }}>
                      <Typography variant="body4">
                        Nenhum acesso no período.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, itemIndex) => (
                    <TableRow
                      hover
                      key={`${item.email}-${item.data_hora}-${itemIndex}`}
                    >
                      <TableCell>{item.email}</TableCell>
                      <TableCell>
                        {formatAccessLogDate(item.data_hora, timeZone)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            labelRowsPerPage="Linhas por página:"
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}
    </div>
  );
}
